import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { House, MapPin, Users, CurrencyDollar, Wrench, ClockCounterClockwise, Warning, CheckCircle,
         Package, Repeat, ArrowSquareOut, Buildings, ClipboardText, Ruler, User as UserIcon,
         Bookmark, X, Plus, PencilSimple, Tag } from "@phosphor-icons/react";
import PropertyNotesCard from "@/components/PropertyNotesCard";
import PropertyDetailsCard from "@/components/PropertyDetailsCard";
import UnitsEditor from "@/components/UnitsEditor";
import PropertyPhotoArchive from "@/components/PropertyPhotoArchive";
import PreventiveMaintenancePanel from "@/components/PreventiveMaintenancePanel";
import PropertyStreetViewCard from "@/components/PropertyStreetViewCard";
import PhillyComplianceModal from "@/components/PhillyComplianceModal";
import RecentInspectionsStrip from "@/components/RecentInspectionsStrip";

export function PropertiesList() {
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [taggedMap, setTaggedMap] = useState({});   // address_key -> { applied_count, total_tags }
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");   // all|open|legal|expiring|group:<slug>|tagged
  useEffect(() => {
    api.get("/properties/list").then(r => setData(r.data)).catch(() => setData({ properties: [], total: 0 }));
    api.get("/property-groups").then(r => setGroups(r.data.groups || [])).catch(() => setGroups([]));
    api.get("/emergency-map/tagged-summary").then(r => {
      const m = {};
      (r.data.tagged || []).forEach(t => {
        const k = t.address_key;
        if (!m[k]) m[k] = { applied_count: 0, total_tags: 0, units: 0 };
        m[k].applied_count += t.applied_count || 0;
        m[k].total_tags += t.total_tags || 0;
        m[k].units += 1;
      });
      setTaggedMap(m);
    }).catch(() => setTaggedMap({}));
  }, []);
  if (!data) return <AdminShell><div className="p-8 font-bold">Loading properties…</div></AdminShell>;

  const applyFilter = (p) => {
    if (filter === "former") return !!p.former;
    if (p.former) return false;
    if (filter === "all") return true;
    if (filter === "open") return p.open_jobs > 0;
    if (filter === "legal") return !!p.has_legal_wo;
    if (filter === "expiring") return p.license_min_days_left !== null && p.license_min_days_left <= 30;
    if (filter === "tagged") return !!taggedMap[p.normalized_address];
    if (filter.startsWith("group:")) return (p.groups || []).includes(filter.slice(6));
    return true;
  };
  const props = (data.properties || []).filter(p => {
    if (!applyFilter(p)) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return (p.address || "").toLowerCase().includes(needle)
      || (p.customers || []).join(" ").toLowerCase().includes(needle)
      || (p.landlord_name || "").toLowerCase().includes(needle)
      || (p.approver_name || "").toLowerCase().includes(needle)
      || (p.approver_email || "").toLowerCase().includes(needle)
      || (p.head_lessee || "").toLowerCase().includes(needle);
  });

  // Count buckets across ALL properties so filter chips show live counts
  const buckets = {
    all: (data.properties || []).filter(p => !p.former).length,
    former: (data.properties || []).filter(p => p.former).length,
    open: (data.properties || []).filter(p => p.open_jobs > 0).length,
    legal: (data.properties || []).filter(p => p.has_legal_wo).length,
    expiring: (data.properties || []).filter(p => p.license_min_days_left !== null && p.license_min_days_left <= 30).length,
    tagged: (data.properties || []).filter(p => !!taggedMap[p.normalized_address]).length,
  };
  const groupCounts = {};
  (data.properties || []).forEach(p => (p.groups || []).forEach(g => {
    groupCounts[g] = (groupCounts[g] || 0) + 1;
  }));

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <Buildings weight="bold" size={28} className="text-slate-700"/>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">Properties</h1>
        </div>
        <p className="mt-1 text-sm text-slate-600 font-medium">
          Every address you've worked. One tap gets you the full history.
        </p>

        <RecentInspectionsStrip/>

        <div className="mt-6">
          <input list="known-addresses" autoComplete="off" value={q} onChange={e => setQ(e.target.value)} placeholder="Search address, landlord (4SBay), approver (Leslie), head-lessee (LaSalle)…"
            data-testid="property-search-input"
            className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="prop-index-filters">
          <FilterChip active={filter==="all"} onClick={() => setFilter("all")} testid="prop-filter-all"
            label="All" count={buckets.all} tone="slate"/>
          <FilterChip active={filter==="open"} onClick={() => setFilter("open")} testid="prop-filter-open"
            label="Open WOs" count={buckets.open} tone="emerald"/>
          <FilterChip active={filter==="legal"} onClick={() => setFilter("legal")} testid="prop-filter-legal"
            label="Legal WO" count={buckets.legal} tone="red"/>
          <FilterChip active={filter==="expiring"} onClick={() => setFilter("expiring")} testid="prop-filter-expiring"
            label="Expiring ≤ 30d" count={buckets.expiring} tone="amber"/>
          {buckets.former > 0 && (
            <FilterChip active={filter==="former"} onClick={() => setFilter("former")} testid="prop-filter-former"
              label="Former (no longer managed)" count={buckets.former} tone="slate"/>
          )}
          {buckets.tagged > 0 && (
            <FilterChip active={filter==="tagged"} onClick={() => setFilter("tagged")} testid="prop-filter-tagged"
              label="Tagged" count={buckets.tagged} tone="violet"/>
          )}
          {groups.filter(g => groupCounts[g.slug] > 0).map(g => (
            <FilterChip key={g.slug} active={filter===`group:${g.slug}`}
              onClick={() => setFilter(`group:${g.slug}`)} testid={`prop-filter-group-${g.slug}`}
              label={g.name} count={groupCounts[g.slug]} color={g.color}/>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-3">
          {props.length === 0 && (
            <div className="col-span-full rugged-card p-8 text-center bg-white" data-testid="properties-empty">
              <Buildings weight="bold" size={40} className="mx-auto text-slate-400"/>
              <div className="mt-2 font-black uppercase text-sm tracking-wider text-slate-500">
                {q ? "No matches" : "No properties yet"}
              </div>
            </div>
          )}
          {(q.trim().length > 0 || filter !== "all") ? (
            props.map(p => {
            const href = p.radar_managed && p.total_jobs === 0
              ? `/admin/projects`
              : `/admin/properties/${encodeURIComponent(p.address)}`;
            return (
            <Link key={p.normalized_address} to={href}
              data-testid={`property-row-${p.normalized_address.slice(0, 40)}`}
              className="rugged-card p-4 bg-white hover:shadow-[6px_6px_0px_#0f172a] transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {p.radar_managed && (
                      <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-cyan-600 text-white inline-flex items-center gap-0.5" data-testid="radar-managed-badge">
                        🎯 Radar-Managed
                      </span>
                    )}
                    {(p.recurring || []).length > 0 && (
                      <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-amber-500 text-white inline-flex items-center gap-0.5">
                        <Repeat weight="bold" size={10}/> Repeat
                      </span>
                    )}
                    {p.unit_count > 1 && (
                      <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-fuchsia-600 text-white inline-flex items-center gap-0.5">
                        <Buildings weight="bold" size={10}/> {p.unit_count} Units
                      </span>
                    )}
                    {p.total_turns > 0 && (
                      <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-amber-600 text-white">{p.total_turns} Turn{p.total_turns === 1 ? "" : "s"}</span>
                    )}
                    {p.open_jobs > 0 && (
                      <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-emerald-600 text-white">{p.open_jobs} Open</span>
                    )}
                    {p.has_legal_wo && (
                      <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-red-600 text-white inline-flex items-center gap-0.5" data-testid={`legal-badge-${p.normalized_address.slice(0,40)}`}>
                        ⚖ Legal
                      </span>
                    )}
                    {taggedMap[p.normalized_address] && (
                      <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-violet-600 text-white inline-flex items-center gap-0.5"
                            data-testid={`tagged-badge-${p.normalized_address.slice(0,40)}`}
                            title={`${taggedMap[p.normalized_address].applied_count} of ${taggedMap[p.normalized_address].total_tags} shut-off tags applied on-site`}>
                        <Tag weight="bold" size={10}/> Tagged
                      </span>
                    )}
                    {p.license_min_days_left !== null && p.license_min_days_left !== undefined && p.license_min_days_left <= 30 && (
                      <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white ${p.license_min_days_left < 0 ? "bg-red-600" : p.license_min_days_left <= 7 ? "bg-red-500" : "bg-amber-500"}`}>
                        {p.license_min_days_left < 0 ? `Expired ${Math.abs(p.license_min_days_left)}d` : `${p.license_min_days_left}d left`}
                      </span>
                    )}
                    {(p.groups || []).slice(0, 3).map(g => (
                      <span key={g} className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-400" data-testid={`prop-row-group-${g}`}>
                        #{g}
                      </span>
                    ))}
                  </div>
                  <div className="mt-1 font-display font-black text-base leading-tight truncate">{p.address}</div>
                  <div className="text-xs text-slate-600 truncate mt-0.5">
                    {p.radar_managed && p.total_jobs === 0
                      ? <>Managed in <span className="font-black text-cyan-700">ProjectRadar</span> · {p.unit_count} units</>
                      : <>{p.total_jobs} job{p.total_jobs === 1 ? "" : "s"} · {(p.customers || []).slice(0, 2).join(", ") || "—"}{p.top_category ? ` · ${p.top_category}` : ""}</>}
                  </div>
                  {(p.landlord_name || p.approver_name) && (
                    <div className="mt-0.5 text-[10px] font-black uppercase tracking-widest text-slate-500 truncate" data-testid={`property-landlord-${p.normalized_address.slice(0,40)}`}>
                      {p.landlord_name && <>🏛 {p.landlord_name}</>}
                      {p.approver_name && <span className="text-cyan-700 ml-1">· 👤 {p.approver_name}</span>}
                    </div>
                  )}
                </div>

                {p.chargeback_total > 0 && (
                  <div className="text-right flex-shrink-0">
                    <div className="text-[10px] font-black uppercase tracking-wider text-red-700">Chargeback</div>
                    <div className="text-sm font-black text-red-700">${p.chargeback_total.toFixed(2)}</div>
                  </div>
                )}
              </div>
            </Link>
            );
          })
          ) : (
            <div className="col-span-full rugged-card p-8 bg-white text-center" data-testid="properties-hero">
              <div className="mx-auto w-14 h-14 rounded-full bg-cyan-50 border-2 border-cyan-500 flex items-center justify-center mb-3">
                <Buildings weight="bold" size={22} className="text-cyan-600"/>
              </div>
              <div className="font-black text-slate-900 text-lg">Search {buckets.all} properties</div>
              <div className="text-[12px] text-slate-500 mt-1">Type an address, landlord, or approver to jump straight to any building.</div>
              <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
                {buckets.open > 0 && (
                  <button onClick={() => setFilter("open")} data-testid="prop-hero-open"
                    className="px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-800 border border-cyan-200 text-[10px] font-black uppercase tracking-widest">
                    Open jobs · {buckets.open}
                  </button>
                )}
                {buckets.legal > 0 && (
                  <button onClick={() => setFilter("legal")} data-testid="prop-hero-legal"
                    className="px-2.5 py-1 rounded-full bg-red-50 text-red-800 border border-red-200 text-[10px] font-black uppercase tracking-widest">
                    Legal WO · {buckets.legal}
                  </button>
                )}
                {buckets.expiring > 0 && (
                  <button onClick={() => setFilter("expiring")} data-testid="prop-hero-expiring"
                    className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-black uppercase tracking-widest">
                    Expiring · {buckets.expiring}
                  </button>
                )}
                <button onClick={() => { setFilter("all"); if (q === "") setQ(" "); }} data-testid="prop-hero-show-all"
                  className="px-2.5 py-1 rounded-full bg-slate-900 text-white border border-slate-900 text-[10px] font-black uppercase tracking-widest">
                  Show all · {buckets.all}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

function FilterChip({ active, onClick, label, count, tone, color, testid }) {
  const toneClasses = {
    slate: active ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500",
    emerald: active ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-emerald-700 border-emerald-400 hover:border-emerald-600",
    red: active ? "bg-red-600 text-white border-red-600" : "bg-white text-red-700 border-red-400 hover:border-red-600",
    amber: active ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-400 hover:border-amber-500",
    violet: active ? "bg-violet-600 text-white border-violet-600" : "bg-white text-violet-700 border-violet-400 hover:border-violet-600",
  };
  const style = color && !toneClasses[tone] ? (active
    ? { backgroundColor: color, color: "white", borderColor: color }
    : { color: color, borderColor: color }) : {};
  return (
    <button onClick={onClick} data-testid={testid} style={style}
      className={`h-8 px-2.5 border-2 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 ${toneClasses[tone] || (active ? "text-white" : "bg-white hover:opacity-80")}`}>
      {label}
      <span className="opacity-70">({count})</span>
    </button>
  );
}

export function PropertyDetail() {
  return (
    <AdminShell>
      <PropertyDetailBody/>
    </AdminShell>
  );
}

// Extracted body — reused by PropertyDetailV2 which wraps this in a
// BrandedHero. Renders WITHOUT its own AdminShell.
export function PropertyDetailBody({ v2 = false }) {
  const { address: encoded } = useParams();
  const address = decodeURIComponent(encoded || "");
  const [data, setData] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null); // null = show whole building
  const [showCompliance, setShowCompliance] = useState(false);

  const reload = () => api.get(`/properties/detail`, { params: { address }})
      .then(r => setData(r.data))
      .catch(() => setData({ found: false }));
  useEffect(() => { reload(); }, [address]);

  const toggleFormer = async () => {
    const making = !data.former;
    const msg = making
      ? `Mark ${address} as NO LONGER MANAGED?\n\n• Hidden from the Properties list and address suggestions (history stays)\n• Its open/pool WOs are archived\n• No notifications`
      : `Mark ${address} as managed again? It will reappear in the Properties list.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post("/properties/set-managed", { address, managed: !making, archive_open_wos: making });
      toast.success(making ? `Marked former · archived ${r.data.archived_wos} open WOs` : "Property is active again");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Update failed")); }
  };

  if (!data) return <div className="p-8 font-bold">Loading property…</div>;
  // Property Portal: even a "cold" address (no jobs, no walks yet) opens
  // its portal — the /properties/ensure endpoint has already upserted a
  // property_config. We render the empty shell so the user can start
  // adding files, notes, notification chain, walks, etc. right away.
  if (!data.found) {
    data.address = data.address || address;
    data.summary = data.summary || {};
    data.units = data.units || [];
    data.jobs = data.jobs || [];
    data.customers = data.customers || [];
  }

  const s = data.summary || {};
  // Single-family (or 1-unit) buildings shouldn't force a click into a
  // "Unit 1" tile just to see the WOs. When there's ≤1 real unit we hide
  // the Units panel and hoist the "All Work Orders" list right below KPIs.
  const _realUnits = (data.units || []).filter(u => u.unit && u.unit.toLowerCase() !== "common");
  const isSingleUnit = _realUnits.length <= 1;

  return (
    <>
      <div className="p-6 sm:p-8 max-w-7xl mx-auto">
        {!v2 && (
          <Link to="/admin/properties" className="text-xs font-black uppercase tracking-wider text-slate-500 hover:text-[#00C4CC]">
            ← All Properties
          </Link>
        )}
        <div className={`${v2 ? "" : "mt-2"} flex items-start justify-between gap-3 flex-wrap`}>
          <div className="min-w-0">
            {!v2 && (
              <div className="flex items-center gap-3">
                <House weight="fill" size={40} className="text-slate-800 hidden sm:block flex-shrink-0"/>
                <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl leading-none break-all tracking-tight" data-testid="prop-hero-address">{data.address}</h1>
              </div>
            )}
            {data.former && (
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest" data-testid="prop-former-badge">
                Former property — no longer managed · history kept for reference
              </div>
            )}
            {((data.landlord_name || "") || (data.customers || []).length > 0) && (
              <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="prop-hero-owner">
                {data.landlord_logo_url && (
                  <img src={data.landlord_logo_url} alt="Landlord logo" className="h-8 w-auto border border-slate-300 bg-white p-0.5"/>
                )}
                <span className="text-sm sm:text-lg font-black uppercase tracking-widest text-slate-700">
                  {data.landlord_name || (data.customers || [])[0] || ""}
                </span>
                {(data.customers || []).length > 0 && (
                  <span className="text-xs sm:text-sm text-slate-500 font-medium">
                    · {(data.customers || []).slice(0, 3).join(" · ")}
                  </span>
                )}
              </div>
            )}
            <RequestRadarLink address={data.address} units={data.units || []} focusUnit={selectedUnit}/>
            <Link to={`/admin/properties/${encodeURIComponent(data.address)}/snapshot`} data-testid="prop-snapshot-btn"
              className="mt-2 h-10 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
              <Buildings weight="fill" size={13}/> Property Snapshot
            </Link>
            <a href={`${API}/properties/sheet.pdf?address=${encodeURIComponent(data.address)}&auth=${encodeURIComponent(localStorage.getItem('access_token') || '')}`}
               target="_blank" rel="noreferrer" data-testid="prop-sheet-pdf-btn"
               title="One-tap PDF of the full Property Portal"
               className="mt-2 h-10 px-3 bg-slate-800 hover:bg-slate-900 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
              <Buildings weight="fill" size={13}/> Print Sheet PDF
            </a>
            <button onClick={toggleFormer} data-testid="prop-former-toggle"
              className={`mt-2 h-10 px-3 ${data.former ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-500 hover:bg-slate-600"} text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1`}>
              {data.former ? "Mark Managed Again" : "No Longer Managed"}
            </button>
            <button onClick={() => setShowCompliance(true)} data-testid="prop-compliance-btn"
              className="mt-2 h-10 px-3 bg-cyan-700 hover:bg-cyan-800 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
              <Buildings weight="fill" size={13}/> Pull Philly Compliance
            </button>
            {/* One-tap prod seed / repair for the 43-unit Chew Flats. Always
                visible on the 5346 Chew page so it doubles as a fix button
                when data is misrouted or vacancy statuses go wrong. */}
            {(data.normalized_address === "5346 chew ave" || /5346\s+chew/i.test(data.address || "")) && (
              <BootstrapChewButton onDone={() => window.location.reload()}/>
            )}
            {/* Bulk Walk shortcut — one tap to the Claude nameplate scanner */}
            <Link to={`/admin/properties/${encodeURIComponent(data.address)}/walk`}
              data-testid="prop-bulk-walk-btn"
              className="mt-2 h-10 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
              <House weight="fill" size={13}/> Bulk Walk
            </Link>
          </div>
          {(data.repeat_alerts || []).length > 0 && (
            <div className="p-2 border-l-4 border-red-600 bg-red-50" data-testid="prop-repeat-alerts">
              <div className="text-[10px] font-black uppercase tracking-wider text-red-900 flex items-center gap-1">
                <Repeat weight="bold" size={12}/> Repeat Issue Alert — 3+ times
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {data.repeat_alerts.map(r => (
                  <span key={r.category} className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-white border-2 border-red-600 text-red-800">
                    {r.category} × {r.count}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(data.recurring_categories || []).filter(r => r.count < 3).length > 0 && (
            <div className="p-2 border-l-4 border-amber-500 bg-amber-50" data-testid="prop-recurring">
              <div className="text-[10px] font-black uppercase tracking-wider text-amber-900 flex items-center gap-1">
                <Repeat weight="bold" size={12}/> Recurring Problems
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {data.recurring_categories.filter(r => r.count < 3).map(r => (
                  <span key={r.category} className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-white border-2 border-amber-500 text-amber-800">
                    {r.category} × {r.count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Visual identification — Street View + block map. Hero position
            so the team sees the actual house the moment they open the portal. */}
        <PropertyStreetViewCard address={data.address}/>

        {/* Summary KPIs */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Total Jobs" value={s.total_jobs || 0} testid="prop-kpi-jobs"/>
          <Kpi label="Turns Done" value={s.total_turns || 0} testid="prop-kpi-turns"/>
          <Kpi label="Chargeback $" value={`$${(s.chargeback_total || 0).toFixed(2)}`} accent="text-red-700" testid="prop-kpi-money"/>
          <Kpi label="Open Jobs" value={s.open_jobs || 0} accent={s.open_jobs > 0 ? "text-amber-600" : "text-slate-500"} testid="prop-kpi-open"/>
        </div>

        {/* Notification Chain — who gets pinged when a WO is created here */}
        <NotificationChainPanel address={data.address}/>

        {/* Single-family shortcut: WO list right below KPIs so you don't have
            to scroll past 12 cards to see the 6 past jobs on this address. */}
        {isSingleUnit && (data.jobs || []).length > 0 && (
          <div className="mt-4 rugged-card p-4 bg-white" data-testid="prop-jobs-shortcut">
            <div className="flex items-center gap-2">
              <ClockCounterClockwise weight="bold" size={20}/>
              <h2 className="font-display font-black text-lg uppercase tracking-wider">All Work Orders</h2>
              <span className="text-xs text-slate-500 font-bold">· {(data.jobs || []).length}</span>
            </div>
            <div className="mt-3 space-y-2" data-testid="prop-jobs-shortcut-list">
              {(data.jobs || []).map(j => (
                <Link key={j.id} to={`/admin/jobs/${j.id}`} data-testid={`prop-shortcut-job-${j.id}`}
                  className="block p-3 border border-slate-300 hover:border-[#0f172a] hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] font-black uppercase tracking-wider font-mono bg-[#0f172a] text-white px-1.5 py-0.5">{j.work_order_number || "—"}</span>
                        {(j.category || j.issue) && (<span className="text-[10px] font-black uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5">{j.category || j.issue}</span>)}
                        {j.source === "appfolio" && (<span className="text-[10px] font-black uppercase tracking-wider bg-white border border-slate-400 text-slate-600 px-1.5 py-0.5">AppFolio</span>)}
                        {j.status === "completed" ? (
                          <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 inline-flex items-center gap-0.5"><CheckCircle weight="fill" size={9}/> Done</span>
                        ) : (
                          <span className="text-[10px] font-black uppercase tracking-wider bg-slate-500 text-white px-1.5 py-0.5">{j.status}</span>
                        )}
                      </div>
                      <div className="mt-1 text-sm font-bold truncate">{j.title || "—"}</div>
                      <div className="mt-0.5 text-[10px] text-slate-500">
                        {(j.completed_at || j.created_at || "").slice(0, 10)}{(j.assigned_to_name || j.vendor_name) ? ` · ${j.assigned_to_name || j.vendor_name}` : ""}
                      </div>
                    </div>
                    <ArrowSquareOut weight="bold" size={14} className="text-slate-400 flex-shrink-0 mt-1"/>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Multi-family Units breakdown — hidden on single-unit properties */}
        {!isSingleUnit && (
          <PropertyUnitsPanel data={data} selectedUnit={selectedUnit} setSelectedUnit={setSelectedUnit} s={s}/>
        )}

        {/* Pinned notes (editable) */}
        <div className="mt-4">
          <PropertyNotesCard address={data.address} readOnly={false}
            initialNotes={data.notes || ""}
            initialMeta={{ at: data.notes_updated_at, by: data.notes_updated_by_name }}/>
        </div>

        {/* AppFolio-parity Property Details (Maint Limit, Rental License, Fire Insp, etc.) */}
        <div className="mt-4">
          <PropertyDetailsCard address={data.address}/>
        </div>

        {/* Units & Leases table — feeds the WO sidebar Lease Context chip */}
        <div className="mt-4">
          <UnitsEditor address={data.address}/>
        </div>

        {/* Property size / specs — used to auto-fill sub quote requests */}
        <PropertySpecsCard address={data.address}/>

        {/* Landlord assignment — feeds Client Portal filtering */}
        <PropertyLandlordCard address={data.address} normalized={data.normalized_address}/>

        {/* Portfolio tags — SFH, PMR_Markets, Active_RentCheck, etc. Flows
            into filters across every view. */}
        <PropertyGroupsCard address={data.address} initial={data.groups || []}/>

        {/* Compliance — rental license + fire safety + insurance + warranty
            with expiry-aware chips. Field Radar owns this because AppFolio
            doesn't alert you 30 days out. */}
        <PropertyComplianceCard address={data.address} initial={data.compliance || {}}/>

        {/* Preventive Maintenance — cyclic task tracker (HVAC filters, gutters, etc)
            + cron auto-drafts WOs 14 days before each task falls due. */}
        <PreventiveMaintenancePanel address={data.address}/>

        {/* Marketing media — Matterport tours, 360° photos, YouTube videos,
            gallery links. Techs open one-tap before rolling out. */}
        <PropertyMediaCard address={data.address} initial={data.media || []}/>

        {/* Physical keys inventory + check-out log — who has key 4B, when
            they took it, when they returned it. */}
        <PropertyKeysCard address={data.address} initial={data.keys || []}/>

        {/* Audit log — every activity_log row touching this address. Compliance
            edits, group tag changes, key checkouts, WO creations, walks. */}
        <PropertyAuditLogCard address={data.address}/>

        {/* Quotes / Receipts / Invoices rollup — grand-total property spend
            + drill-down into every receipt (photo) and quote (sub-quote or
            uploaded PDF) attached to any WO or file at this address. */}
        <PropertyFinancialsCard address={data.address}/>

        {/* HVAC / Maintenance info */}
        {(data.maintenance && (data.maintenance.filter_size || data.maintenance.filter_type || data.maintenance.hvac_last_serviced || data.maintenance.maintenance_notes)) && (
          <div className="mt-4 rugged-card p-4 bg-amber-50 border-l-4 border-amber-500" data-testid="prop-maintenance-card">
            <div className="flex items-center gap-2">
              <Wrench weight="bold" size={18} className="text-amber-700"/>
              <h3 className="font-display font-black text-sm uppercase tracking-wider text-amber-900">Property Maintenance</h3>
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              {data.maintenance.filter_size && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Filter Size</div>
                  <div className="font-mono font-black text-base">{data.maintenance.filter_size}</div>
                </div>
              )}
              {data.maintenance.filter_type && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Filter Type</div>
                  <div className="font-bold text-sm">{data.maintenance.filter_type}</div>
                </div>
              )}
              {data.maintenance.hvac_last_serviced && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">HVAC Serviced</div>
                  <div className="font-mono font-black text-sm">{data.maintenance.hvac_last_serviced}</div>
                </div>
              )}
            </div>
            {data.maintenance.maintenance_notes && (
              <div className="mt-2 text-xs text-slate-700 whitespace-pre-wrap">{data.maintenance.maintenance_notes}</div>
            )}
            {data.maintenance.updated_at && (
              <div className="mt-2 text-[10px] font-medium text-slate-500">
                Updated {new Date(data.maintenance.updated_at).toLocaleDateString()} by {data.maintenance.updated_by_name || "—"}
              </div>
            )}
          </div>
        )}

        {/* Service Recommendations aggregated across all jobs */}
        {(data.service_recommendations || []).length > 0 && (
          <div className="mt-4 rugged-card p-4 bg-white border-l-4 border-red-500" data-testid="prop-recs-card">
            <div className="flex items-center gap-2">
              <ClipboardText weight="bold" size={18} className="text-red-600"/>
              <h3 className="font-display font-black text-sm uppercase tracking-wider">Service Recommendations</h3>
              <span className="text-xs text-slate-500 font-bold">· {data.service_recommendations.length} across all jobs</span>
            </div>
            <ul className="mt-3 space-y-2">
              {data.service_recommendations.map(r => {
                const pri = r.priority || "normal";
                const pillClass = pri === "high" ? "bg-red-600 text-white" : pri === "low" ? "bg-slate-500 text-white" : "bg-amber-500 text-white";
                return (
                  <li key={r.id} data-testid={`prop-reco-${r.id}`}
                    className="p-3 border-2 border-slate-300 hover:border-[#0f172a] transition-colors">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${pillClass}`}>{pri}</span>
                      {r.category && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-[#0f172a] text-white">{r.category}</span>}
                      <span className="font-black text-sm flex-1 min-w-0">{r.title}</span>
                      <Link to={`/admin/jobs/${r.job_id}`} className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC] hover:underline whitespace-nowrap">
                        View job →
                      </Link>
                    </div>
                    {r.notes && <p className="mt-1 text-xs text-slate-600 whitespace-pre-wrap">{r.notes}</p>}
                    <div className="mt-1 text-[10px] text-slate-500 font-medium">
                      {r.created_by_name || "—"} · {(r.created_at || "").slice(0, 10)} · {r.job_title || "—"}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Photo archive across all past jobs */}
        <div className="mt-4">
          <PropertyPhotoArchive address={data.address}/>
        </div>

        <div className={`mt-6 grid grid-cols-1 ${isSingleUnit ? "lg:grid-cols-1" : "lg:grid-cols-3"} gap-4`}>
          {/* Left: Jobs timeline — hidden on single-unit (rendered up top instead) */}
          {!isSingleUnit && (
          <div className="lg:col-span-2 rugged-card p-4 bg-white">
            <div className="flex items-center gap-2">
              <ClockCounterClockwise weight="bold" size={20}/>
              <h2 className="font-display font-black text-lg uppercase tracking-wider">All Work Orders</h2>
              <span className="text-xs text-slate-500 font-bold">· {(data.jobs || []).length}</span>
            </div>
            <div className="mt-3 space-y-2" data-testid="prop-jobs-list">
              {(data.jobs || []).map(j => (
                <Link key={j.id} to={`/admin/jobs/${j.id}`} data-testid={`prop-job-${j.id}`}
                  className="block p-3 border border-slate-300 hover:border-[#0f172a] hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] font-black uppercase tracking-wider font-mono bg-[#0f172a] text-white px-1.5 py-0.5">{j.work_order_number || "—"}</span>
                        {j.unit && (<span className="text-[10px] font-black uppercase tracking-wider bg-fuchsia-600 text-white px-1.5 py-0.5">#{j.unit}</span>)}
                        {(j.category || j.issue) && (<span className="text-[10px] font-black uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5">{j.category || j.issue}</span>)}
                        {j.source === "appfolio" && (<span className="text-[10px] font-black uppercase tracking-wider bg-white border border-slate-400 text-slate-600 px-1.5 py-0.5">AppFolio</span>)}
                        {j.is_turn && (<span className="text-[10px] font-black uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5">Turn ${j.turn_flat_price.toFixed(0)}</span>)}
                        {j.status === "completed" ? (
                          <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 inline-flex items-center gap-0.5"><CheckCircle weight="fill" size={9}/> Done</span>
                        ) : (
                          <span className="text-[10px] font-black uppercase tracking-wider bg-slate-500 text-white px-1.5 py-0.5">{j.status}</span>
                        )}
                      </div>
                      <div className="mt-1 text-sm font-bold truncate">{j.title || "—"}</div>
                      {j.completion_notes && <div className="mt-0.5 text-xs text-slate-600 line-clamp-2">{j.completion_notes}</div>}
                      <div className="mt-0.5 text-[10px] text-slate-500">
                        {(j.completed_at || j.created_at || "").slice(0, 10)}{(j.assigned_to_name || j.vendor_name) ? ` · ${j.assigned_to_name || j.vendor_name}` : ""}
                      </div>
                    </div>
                    <ArrowSquareOut weight="bold" size={14} className="text-slate-400 flex-shrink-0 mt-1"/>
                  </div>
                </Link>
              ))}
              {((data.jobs || []).filter(j => selectedUnit === null || (j.unit || "") === selectedUnit)).length === 0 && (
                <div className="text-sm text-slate-500 font-bold">
                  {selectedUnit !== null ? `No work orders for Unit ${selectedUnit || "(base)"} yet.` : "No work orders yet."}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Right: Appliances + Workers + Chargeback */}
          <div className="space-y-4">
            {/* Appliances */}
            <div className="rugged-card p-4 bg-white">
              <div className="flex items-center gap-2">
                <Package weight="bold" size={18}/>
                <h3 className="font-display font-black text-sm uppercase tracking-wider">Appliances</h3>
                <span className="text-xs text-slate-500 font-bold ml-auto">{(data.appliances || []).length}</span>
              </div>
              {(data.appliances || []).length === 0 && <div className="mt-2 text-xs text-slate-500 font-bold">None documented yet.</div>}
              <div className="mt-2 space-y-1.5">
                {(data.appliances || []).map((a, i) => (
                  <div key={i} className="p-2 border border-slate-200 text-xs" data-testid={`prop-appliance-${i}`}>
                    <div className="font-black uppercase tracking-wider text-[11px]">{a.type || "—"}</div>
                    <div className="text-slate-600">{[a.brand, a.model].filter(Boolean).join(" · ") || "—"}</div>
                    {a.estimated_age_years && <div className="text-[10px] text-slate-500 mt-0.5">~{a.estimated_age_years}y old</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Workers who touched this property */}
            <div className="rugged-card p-4 bg-white">
              <div className="flex items-center gap-2">
                <Users weight="bold" size={18}/>
                <h3 className="font-display font-black text-sm uppercase tracking-wider">Crew History</h3>
                <span className="text-xs text-slate-500 font-bold ml-auto">{(data.workers || []).length}</span>
              </div>
              <div className="mt-2 space-y-1">
                {(data.workers || []).map(w => (
                  <div key={w.name} className="flex items-center justify-between text-xs" data-testid={`prop-worker-${w.name}`}>
                    <span className="font-bold truncate">{w.name}</span>
                    <span className="text-slate-500 font-mono">{w.job_count}×</span>
                  </div>
                ))}
                {(data.workers || []).length === 0 && <div className="text-xs text-slate-500 font-bold">No workers assigned yet.</div>}
              </div>
            </div>

            {/* Chargeback items */}
            {(data.chargeback_items || []).length > 0 && (
              <div className="rugged-card p-4 bg-white border-l-4 border-red-600">
                <div className="flex items-center gap-2">
                  <CurrencyDollar weight="bold" size={18} className="text-red-700"/>
                  <h3 className="font-display font-black text-sm uppercase tracking-wider text-red-700">Tenant Damages</h3>
                  <span className="text-xs text-slate-500 font-bold ml-auto">${(s.chargeback_total || 0).toFixed(0)}</span>
                </div>
                <div className="mt-2 space-y-1.5 max-h-[300px] overflow-y-auto">
                  {data.chargeback_items.map((it, i) => (
                    <Link key={i} to={`/admin/jobs/${it.job_id}`}
                      className="block p-2 border border-slate-200 hover:border-red-500 text-xs" data-testid={`prop-cb-item-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-black uppercase tracking-wider text-[10px]">${it.office_deduction_amount.toFixed(2)}</span>
                        {it.office_reviewed
                          ? <span className="text-[9px] font-black uppercase text-emerald-700">Reviewed</span>
                          : <span className="text-[9px] font-black uppercase text-amber-600">Pending</span>}
                      </div>
                      <div className="mt-0.5 text-slate-700 truncate">{it.damage_description || "—"}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showCompliance && (
        <PhillyComplianceModal address={address} onClose={() => setShowCompliance(false)}/>
      )}
    </>
  );
}

function RequestRadarLink({ address, units = [], focusUnit = null }) {
  // Extract distinct unit strings from the property's history. Include an
  // implicit "" bucket when the building itself was ever booked without a unit.
  const unitList = (() => {
    const set = new Map();
    (units || []).forEach((u) => {
      const key = (u.unit || "").trim();
      set.set(key, (set.get(key) || 0) + (u.job_count || u.total || 0));
    });
    if (!set.size) set.set("", 0);
    return Array.from(set.keys()).sort((a, b) => (a === "" ? -1 : b === "" ? 1 : a.localeCompare(b, undefined, { numeric: true })));
  })();
  const [links, setLinks] = useState({}); // unit -> url
  const [busy, setBusy] = useState(null); // unit currently loading

  const load = async (unit) => {
    setBusy(unit);
    try {
      const q = `address=${encodeURIComponent(address)}${unit ? `&unit=${encodeURIComponent(unit)}` : ""}`;
      const r = await api.get(`/properties/request-radar-token?${q}`);
      setLinks((prev) => ({ ...prev, [unit]: `${window.location.origin}/report/${r.data.token}` }));
    } catch (e) { toast.error("Failed to load link"); }
    finally { setBusy(null); }
  };
  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("RequestRadar™ link copied — text it to the tenant or use on-site");
  };
  const rotate = async (unit) => {
    if (!window.confirm(`Rotate the token for ${unit ? `unit ${unit}` : "this address"}?\nThe old link will stop working immediately.`)) return;
    setBusy(unit);
    try {
      const q = `address=${encodeURIComponent(address)}${unit ? `&unit=${encodeURIComponent(unit)}` : ""}`;
      const r = await api.post(`/properties/request-radar-token/regenerate?${q}`);
      setLinks((prev) => ({ ...prev, [unit]: `${window.location.origin}/report/${r.data.token}` }));
      toast.success("Token rotated — old link no longer works");
    } catch (e) { toast.error("Could not rotate"); }
    finally { setBusy(null); }
  };

  return (
    <div className="mt-3" data-testid="request-radar-link-wrap">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
        🤖 Maintenance Helper · one link per unit (AI triage + request form)
      </div>
      <div className={unitList.length > 8
        ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5"
        : "space-y-1.5"}>
        {unitList.map((unit) => {
          const url = links[unit];
          const testUnit = (unit || "base").toLowerCase().replace(/\s+/g, "-");
          return (
            <div key={unit || "_base"} className={`flex flex-wrap items-center gap-1.5 p-1 -m-1 ${focusUnit !== null && focusUnit === unit ? "bg-cyan-100 border-2 border-[#00C4CC]" : ""}`} data-testid={`rr-unit-row-${testUnit}`}>
              <span className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest border-2 ${unit ? "border-fuchsia-600 text-fuchsia-800 bg-fuchsia-50" : "border-slate-400 text-slate-600 bg-slate-50"}`}>
                {unit ? `Unit ${unit}` : "Building (no unit)"}
              </span>
              {!url ? (
                <button onClick={() => load(unit)} disabled={busy === unit} data-testid={`rr-load-${testUnit}`}
                  className="h-7 px-2 border-2 border-fuchsia-500 text-fuchsia-800 hover:bg-fuchsia-500 hover:text-white font-black uppercase text-[10px] tracking-widest">
                  {busy === unit ? "…" : "🎯 Get Link"}
                </button>
              ) : (
                <>
                  <span className="text-[10px] font-mono bg-slate-100 border border-slate-300 px-2 py-1 max-w-[220px] truncate" data-testid={`rr-url-${testUnit}`}>{url}</span>
                  <button onClick={() => copy(url)} data-testid={`rr-copy-${testUnit}`}
                    className="h-7 px-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest">
                    Copy
                  </button>
                  <a href={`${api.defaults.baseURL}/properties/qr-sheet?address=${encodeURIComponent(address)}${unit ? `&unit=${encodeURIComponent(unit)}` : ""}&auth=${encodeURIComponent(localStorage.getItem("access_token") || localStorage.getItem("token") || "")}`}
                     target="_blank" rel="noreferrer" data-testid={`rr-qr-${testUnit}`}
                     className="h-7 px-2 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                    🖨 QR
                  </a>
                  <button onClick={() => rotate(unit)} disabled={busy === unit} data-testid={`rr-rotate-${testUnit}`}
                    className="h-7 px-2 border-2 border-slate-300 hover:border-red-500 hover:text-red-600 text-slate-600 font-black uppercase text-[10px] tracking-widest">
                    ↻ Rotate
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NotificationChainPanel({ address }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!address) return;
    api.get("/property-notification-chain", { params: { address }})
      .then(r => setData(r.data)).catch(() => setData(null));
  }, [address]);
  if (!data || !(data.chain || []).length) return null;
  const saveOne = async (i, patch) => {
    const chain = data.chain.map((c, idx) => idx === i ? { ...c, ...patch } : c);
    setBusy(true);
    try {
      await api.patch(`/landlords/${data.landlord_id}/notification-chain`, chain);
      setData(d => ({ ...d, chain }));
      toast.success("Chain updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 p-4 bg-white border-2 border-amber-400" data-testid="notif-chain-panel">
      <div className="flex items-center gap-2 mb-1">
        <Buildings weight="fill" size={18} className="text-amber-600"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">
          Notification Chain · {data.landlord_name} ({data.role_label})
        </h3>
      </div>
      <p className="text-[10px] text-slate-500 leading-snug mb-3">
        Every new work order at this property is routed to these people in order.
        Update .PLACEHOLDER emails below with real addresses.
      </p>
      <div className="space-y-2">
        {data.chain.map((r, i) => (
          <div key={i} className="p-2 bg-slate-50 border border-slate-300"
            data-testid={`notif-recipient-${i}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 flex items-center justify-center bg-amber-500 text-white font-black text-xs">
                {r.sequence}
              </span>
              <span className="font-black text-sm">{r.name}</span>
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-auto">
                {r.role.replace(/_/g, " ")} · +{r.delay_min}min
              </span>
            </div>
            <input type="email" defaultValue={r.email}
              onBlur={e => e.target.value !== r.email && saveOne(i, { email: e.target.value })}
              disabled={busy}
              data-testid={`notif-email-${i}`}
              className="w-full h-9 px-2 text-xs border border-slate-300 focus:border-amber-500 focus:outline-none font-mono"/>
            {r.email.includes("PLACEHOLDER") && (
              <div className="text-[9px] text-red-600 font-black uppercase tracking-widest mt-0.5">⚠ Placeholder — update with real email</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BootstrapChewButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Seed / repair 5346 Chew (43 units)?\n\nThis:\n • Creates any missing unit rows\n • Removes any legacy 5C row\n • Clears auto-defaulted 'occupied' statuses (only where you never set a move-in date)\n • Moves misplaced assets back to their correct unit based on the location tag\n\nSafe to run multiple times.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/bootstrap/chew-units");
      const d = r.data || {};
      toast.success(
        `Seed: ${d.created} new · ${d.existing} kept · 5C removed: ${d.removed_invalid || 0} · vacancy reset: ${d.vacancy_reset || 0} · assets moved: ${d.assets_moved || 0} · total ${d.total}`,
        { duration: 6000 }
      );
      setTimeout(() => onDone && onDone(), 1400);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Bootstrap failed"));
      setBusy(false);
    }
  };
  return (
    <button onClick={run} disabled={busy} data-testid="prop-bootstrap-chew-btn"
      className="mt-2 h-10 px-3 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-400 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] inline-flex items-center gap-1">
      <Buildings weight="fill" size={13}/> {busy ? "Repairing…" : "Seed / Repair Chew"}
    </button>
  );
}

function Kpi({ label, value, testid, accent = "text-[#0f172a]" }) {
  return (
    <div className="rugged-card p-4 bg-white" data-testid={testid}>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-display font-black text-2xl sm:text-3xl ${accent}`}>{value}</div>
    </div>
  );
}

// ------ Editable property size / specs — feeds sub-quote requests
function PropertySpecsCard({ address }) {
  const [specs, setSpecs] = useState({ sq_ft: "", stories: "", year_built: "", lot_notes: "" });
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!address) return;
    api.get("/properties/specs", { params: { address }})
      .then(r => {
        setSpecs({
          sq_ft: r.data?.sq_ft ?? "", stories: r.data?.stories ?? "",
          year_built: r.data?.year_built ?? "", lot_notes: r.data?.lot_notes || "",
        });
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [address]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/properties/specs", {
        address,
        sq_ft: specs.sq_ft === "" ? null : Number(specs.sq_ft),
        stories: specs.stories === "" ? null : Number(specs.stories),
        year_built: specs.year_built === "" ? null : Number(specs.year_built),
        lot_notes: specs.lot_notes || "",
      });
      toast.success("Property specs saved");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not save specs"));
    } finally { setBusy(false); }
  };
  if (!loaded) return null;
  const hasAny = specs.sq_ft || specs.stories || specs.year_built || specs.lot_notes;
  return (
    <div className="mt-4 rugged-card p-4 bg-white" data-testid="property-specs-card">
      <div className="flex items-center gap-2">
        <Ruler weight="bold" size={18} className="text-[#0f172a]"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Property Size & Specs</h3>
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Feeds sub quote requests</span>
        {!editing && (
          <button onClick={() => setEditing(true)} data-testid="specs-edit-btn"
            className="ml-auto text-xs font-black uppercase tracking-widest text-[#00C4CC] hover:underline">Edit</button>
        )}
      </div>
      {editing ? (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Sq Ft</span>
            <input type="number" min="0" max="200000" value={specs.sq_ft}
              onChange={(e) => setSpecs({ ...specs, sq_ft: e.target.value })}
              data-testid="specs-sqft"
              className="mt-1 w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-bold bg-white"/>
          </label>
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Stories</span>
            <input type="number" min="0" max="20" value={specs.stories}
              onChange={(e) => setSpecs({ ...specs, stories: e.target.value })}
              data-testid="specs-stories"
              className="mt-1 w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-bold bg-white"/>
          </label>
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Year Built</span>
            <input type="number" min="1700" max="2100" value={specs.year_built}
              onChange={(e) => setSpecs({ ...specs, year_built: e.target.value })}
              data-testid="specs-year"
              className="mt-1 w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-bold bg-white"/>
          </label>
          <label className="block col-span-2 sm:col-span-4">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Lot / Access notes</span>
            <input type="text" maxLength={500} value={specs.lot_notes}
              onChange={(e) => setSpecs({ ...specs, lot_notes: e.target.value })}
              data-testid="specs-lot"
              placeholder="e.g. Corner lot, detached garage, steep driveway"
              className="mt-1 w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-medium bg-white"/>
          </label>
          <div className="col-span-2 sm:col-span-4 flex gap-2">
            <button onClick={() => setEditing(false)} disabled={busy} data-testid="specs-cancel"
              className="h-10 px-3 border-2 border-[#0f172a] bg-white font-black uppercase text-xs tracking-widest disabled:opacity-60">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="specs-save"
              className="flex-1 h-10 bg-[#00C4CC] hover:bg-[#00a8af] text-white font-black uppercase text-xs tracking-widest disabled:opacity-60">
              {busy ? "Saving…" : "Save Specs"}
            </button>
          </div>
        </div>
      ) : (
        hasAny ? (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {specs.sq_ft && (
              <div><div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Sq Ft</div><div className="font-display font-black text-xl">{Number(specs.sq_ft).toLocaleString()}</div></div>
            )}
            {specs.stories && (
              <div><div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Stories</div><div className="font-display font-black text-xl">{specs.stories}</div></div>
            )}
            {specs.year_built && (
              <div><div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Year Built</div><div className="font-display font-black text-xl">{specs.year_built}</div></div>
            )}
            {specs.lot_notes && (
              <div className="col-span-2 sm:col-span-4"><div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Lot / Access</div><div className="mt-0.5 text-sm">{specs.lot_notes}</div></div>
            )}
          </div>
        ) : (
          <p className="mt-2 text-xs text-slate-500 font-medium">No specs on file yet. Add them once — every future sub quote request auto-includes size + year + lot notes.</p>
        )
      )}
    </div>
  );
}

function PropertyLandlordCard({ address, normalized }) {
  const [landlords, setLandlords] = useState([]);
  const [current, setCurrent] = useState(null); // {id, name}
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [llRes, cfgRes] = await Promise.all([
        api.get("/landlords"),
        api.get("/property-config", { params: { address } }).catch(() => ({ data: {} })),
      ]);
      setLandlords(llRes.data || []);
      // Also fetch full property detail for landlord_id (property-config doesn't expose it)
      const full = await api.get("/properties/detail", { params: { address } }).catch(() => ({ data: {} }));
      const lid = full.data?.landlord_id || cfgRes.data?.landlord_id || null;
      if (lid) {
        const match = (llRes.data || []).find(l => l.id === lid);
        setCurrent(match ? { id: match.id, name: match.name } : null);
      } else {
        setCurrent(null);
      }
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [address]);

  const assign = async (llId) => {
    setBusy(true);
    try {
      const { data } = await api.put("/property-landlord", { address, landlord_id: llId || null });
      if (data.assigned) {
        toast.success(`Assigned to ${data.landlord_name}`);
        setCurrent({ id: data.landlord_id, name: data.landlord_name });
      } else {
        toast.success("Landlord unassigned");
        setCurrent(null);
      }
    } catch (e) {
      toast.error("Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rugged-card p-4 bg-white" data-testid="prop-landlord-card">
      <div className="flex items-center gap-2">
        <UserIcon weight="bold" size={18} className="text-[#00C4CC]"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Landlord</h3>
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Controls Client Portal visibility</span>
      </div>
      {loading ? (
        <div className="mt-2 text-xs text-slate-500 font-medium">Loading…</div>
      ) : landlords.length === 0 ? (
        <div className="mt-2 text-xs text-slate-500 font-medium">
          No landlords yet. Add one in{" "}
          <Link to="/admin/landlords" className="text-[#00C4CC] font-black uppercase tracking-widest hover:underline">Landlords →</Link>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <select
            value={current?.id || ""}
            disabled={busy}
            onChange={(e) => assign(e.target.value)}
            data-testid="prop-landlord-select"
            className="h-10 px-3 border-2 border-[#0f172a] text-sm font-medium bg-white min-w-[240px]">
            <option value="">— Not assigned —</option>
            {landlords.map(ll => (
              <option key={ll.id} value={ll.id}>{ll.name}</option>
            ))}
          </select>
          {current && (
            <span className="text-xs font-black uppercase tracking-widest text-slate-600" data-testid="prop-landlord-current">
              Assigned · {current.name}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Property Compliance Card ───────────────────────────────────────
   Rental license · Fire safety · Insurance · Home warranty + Maintenance
   limit. Every date field renders as a color-coded chip based on the
   backend-computed `*_days_left` (server side to avoid TZ drift). AppFolio
   surfaces this info but doesn't alert on expiry — that's Field Radar's job.
─────────────────────────────────────────────────────────────────────── */
function ComplianceChip({ label, days, dateStr, testid }) {
  if (dateStr === undefined || dateStr === null || dateStr === "") return null;
  const sev = days === null || days === undefined
    ? { cls: "bg-slate-100 text-slate-700 border-slate-300", tag: "—" }
    : days < 0 ? { cls: "bg-red-100 text-red-800 border-red-500", tag: `Expired ${Math.abs(days)}d` }
    : days <= 7 ? { cls: "bg-red-100 text-red-800 border-red-500", tag: `${days}d left` }
    : days <= 30 ? { cls: "bg-amber-100 text-amber-800 border-amber-500", tag: `${days}d left` }
    : days <= 60 ? { cls: "bg-yellow-100 text-yellow-800 border-yellow-500", tag: `${days}d left` }
    : { cls: "bg-emerald-50 text-emerald-800 border-emerald-500", tag: `${days}d left` };
  return (
    <div className={`px-2 py-1.5 border-2 ${sev.cls}`} data-testid={testid}>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-mono font-black text-sm leading-none mt-0.5">{dateStr || "—"}</div>
      <div className="text-[10px] font-black uppercase tracking-widest mt-0.5">{sev.tag}</div>
    </div>
  );
}

function PropertyComplianceCard({ address, initial }) {
  const [c, setC] = useState(initial || {});
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    rental_license_number: c.rental_license_number || "",
    rental_license_expiration: c.rental_license_expiration || "",
    rental_license_note: c.rental_license_note || "",
    fire_safety_inspected_at: c.fire_safety_inspected_at || "",
    fire_safety_expiration: c.fire_safety_expiration || "",
    insurance_carrier: c.insurance_carrier || "",
    insurance_expiration: c.insurance_expiration || "",
    home_warranty_expiration: c.home_warranty_expiration || "",
    water_bill_access_code: c.water_bill_access_code || "",
    maintenance_limit_dollars: ((c.maintenance_limit_cents || 0) / 100).toFixed(2),
    unit_entry_preauthorized: !!c.unit_entry_preauthorized,
  });
  const [saving, setSaving] = useState(false);

  const worstDays = [
    c.rental_license_days_left, c.fire_safety_days_left,
    c.insurance_days_left, c.home_warranty_days_left,
  ].filter(x => x !== null && x !== undefined);
  const worst = worstDays.length ? Math.min(...worstDays) : null;
  const banner = worst === null ? "border-slate-300 bg-white"
    : worst < 0 ? "border-red-500 bg-red-50"
    : worst <= 7 ? "border-red-500 bg-red-50"
    : worst <= 30 ? "border-amber-500 bg-amber-50"
    : worst <= 60 ? "border-yellow-500 bg-yellow-50"
    : "border-emerald-500 bg-emerald-50";

  const save = async () => {
    setSaving(true);
    try {
      const cents = Math.round(parseFloat(form.maintenance_limit_dollars || "0") * 100) || 0;
      const r = await api.put("/property-compliance", {
        address,
        rental_license_number: form.rental_license_number,
        rental_license_expiration: form.rental_license_expiration,
        rental_license_note: form.rental_license_note,
        fire_safety_inspected_at: form.fire_safety_inspected_at,
        fire_safety_expiration: form.fire_safety_expiration,
        insurance_carrier: form.insurance_carrier,
        insurance_expiration: form.insurance_expiration,
        home_warranty_expiration: form.home_warranty_expiration,
        water_bill_access_code: form.water_bill_access_code,
        maintenance_limit_cents: cents,
        unit_entry_preauthorized: !!form.unit_entry_preauthorized,
      });
      setC(r.data.compliance);
      setEditing(false);
      toast.success("Compliance saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  const hasAny = c.rental_license_number || c.rental_license_expiration
    || c.fire_safety_expiration || c.insurance_expiration || c.home_warranty_expiration
    || c.water_bill_access_code || (c.maintenance_limit_cents || 0) > 0;

  return (
    <div className={`mt-4 rugged-card p-4 border-l-4 ${banner}`} data-testid="prop-compliance-card">
      <div className="flex items-center gap-2 flex-wrap">
        <ClipboardText weight="bold" size={18} className="text-slate-800"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Compliance</h3>
        {worst !== null && worst < 0 && (
          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white">Overdue</span>
        )}
        {worst !== null && worst >= 0 && worst <= 30 && (
          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-amber-600 text-white">Expiring soon</span>
        )}
        <button onClick={() => setEditing(v => !v)}
          data-testid="prop-compliance-edit-btn"
          className="ml-auto h-8 px-2 text-[10px] font-black uppercase tracking-widest border-2 border-slate-300 hover:bg-white inline-flex items-center gap-1">
          {editing ? "Cancel" : (hasAny ? "Edit" : "Add Compliance")}
        </button>
      </div>

      {!editing && !hasAny && (
        <div className="mt-2 text-xs text-slate-500 font-bold">
          Track rental license, fire safety, insurance, and warranty expirations here. We'll flag anything within 60 days.
        </div>
      )}

      {!editing && hasAny && (
        <>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <ComplianceChip label="Rental License" dateStr={c.rental_license_expiration}
              days={c.rental_license_days_left} testid="prop-compl-license"/>
            <ComplianceChip label="Fire Safety" dateStr={c.fire_safety_expiration}
              days={c.fire_safety_days_left} testid="prop-compl-fire"/>
            <ComplianceChip label="Insurance" dateStr={c.insurance_expiration}
              days={c.insurance_days_left} testid="prop-compl-insurance"/>
            <ComplianceChip label="Home Warranty" dateStr={c.home_warranty_expiration}
              days={c.home_warranty_days_left} testid="prop-compl-warranty"/>
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {c.rental_license_number && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">License #</div>
                <div className="font-mono font-black">{c.rental_license_number}</div>
              </div>
            )}
            {c.insurance_carrier && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Carrier</div>
                <div className="font-bold">{c.insurance_carrier}</div>
              </div>
            )}
            {c.water_bill_access_code && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Water Bill Access</div>
                <div className="font-mono font-black">{c.water_bill_access_code}</div>
              </div>
            )}
            {(c.maintenance_limit_cents || 0) > 0 && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Maintenance Limit</div>
                <div className="font-mono font-black">${(c.maintenance_limit_cents / 100).toFixed(2)}</div>
                <div className="text-[9px] text-slate-500">under-limit WOs auto-approved</div>
              </div>
            )}
            {c.unit_entry_preauthorized && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Entry</div>
                <div className="font-black text-emerald-700">Pre-authorized</div>
              </div>
            )}
          </div>
          {c.rental_license_note && (
            <div className="mt-2 text-xs text-slate-700 whitespace-pre-wrap">📌 {c.rental_license_note}</div>
          )}
          {c.updated_at && (
            <div className="mt-2 text-[10px] font-medium text-slate-500">
              Updated {new Date(c.updated_at).toLocaleDateString()} by {c.updated_by_name || "—"}
            </div>
          )}
        </>
      )}

      {editing && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">License #</div>
            <input value={form.rental_license_number} data-testid="prop-compl-license-input"
              onChange={e => setForm({...form, rental_license_number: e.target.value})}
              className="mt-0.5 h-9 w-full px-2 border-2 border-slate-300 text-sm font-mono"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">License Expiration</div>
            <input type="date" value={form.rental_license_expiration} data-testid="prop-compl-license-exp-input"
              onChange={e => setForm({...form, rental_license_expiration: e.target.value})}
              className="mt-0.5 h-9 w-full px-2 border-2 border-slate-300 text-sm font-mono"/>
          </label>
          <label className="block sm:col-span-2">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">License Note</div>
            <input value={form.rental_license_note} data-testid="prop-compl-license-note-input"
              onChange={e => setForm({...form, rental_license_note: e.target.value})}
              placeholder="renewal pending, owner has it, etc."
              className="mt-0.5 h-9 w-full px-2 border-2 border-slate-300 text-sm"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Fire Safety Inspected</div>
            <input type="date" value={form.fire_safety_inspected_at}
              onChange={e => setForm({...form, fire_safety_inspected_at: e.target.value})}
              className="mt-0.5 h-9 w-full px-2 border-2 border-slate-300 text-sm font-mono"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Fire Safety Expiration</div>
            <input type="date" value={form.fire_safety_expiration} data-testid="prop-compl-fire-exp-input"
              onChange={e => setForm({...form, fire_safety_expiration: e.target.value})}
              className="mt-0.5 h-9 w-full px-2 border-2 border-slate-300 text-sm font-mono"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Insurance Carrier</div>
            <input value={form.insurance_carrier}
              onChange={e => setForm({...form, insurance_carrier: e.target.value})}
              className="mt-0.5 h-9 w-full px-2 border-2 border-slate-300 text-sm"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Insurance Expiration</div>
            <input type="date" value={form.insurance_expiration} data-testid="prop-compl-insurance-exp-input"
              onChange={e => setForm({...form, insurance_expiration: e.target.value})}
              className="mt-0.5 h-9 w-full px-2 border-2 border-slate-300 text-sm font-mono"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Home Warranty Expiration</div>
            <input type="date" value={form.home_warranty_expiration}
              onChange={e => setForm({...form, home_warranty_expiration: e.target.value})}
              className="mt-0.5 h-9 w-full px-2 border-2 border-slate-300 text-sm font-mono"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Water Bill Access Code</div>
            <input value={form.water_bill_access_code}
              onChange={e => setForm({...form, water_bill_access_code: e.target.value})}
              className="mt-0.5 h-9 w-full px-2 border-2 border-slate-300 text-sm font-mono"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Maintenance Limit ($)</div>
            <input type="number" step="1" min="0" value={form.maintenance_limit_dollars}
              data-testid="prop-compl-maint-limit-input"
              onChange={e => setForm({...form, maintenance_limit_dollars: e.target.value})}
              placeholder="500"
              className="mt-0.5 h-9 w-full px-2 border-2 border-slate-300 text-sm font-mono"/>
            <div className="text-[9px] text-slate-500 mt-0.5">Under-limit WOs auto-approved without owner sign-off.</div>
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={form.unit_entry_preauthorized}
              onChange={e => setForm({...form, unit_entry_preauthorized: e.target.checked})}/>
            <span>Unit entry pre-authorized (techs don't need to schedule with tenant)</span>
          </label>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <button onClick={() => setEditing(false)}
              className="h-9 px-3 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest hover:bg-white">
              Cancel
            </button>
            <button onClick={save} disabled={saving} data-testid="prop-compliance-save-btn"
              className="h-9 px-3 bg-[#0f172a] hover:bg-slate-800 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest">
              {saving ? "Saving…" : "Save Compliance"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Property Groups Card ───────────────────────────────────────────
   Portfolio tags (SFH, PMR_Markets, Active_RentCheck, etc.). Assigning a
   tag flows through to every filter downstream — jobs, seasonal, legal
   radar. The card lists current tags with quick-remove x's, offers a
   picker for existing groups, and lets the office coin new groups inline. */
function PropertyGroupsCard({ address, initial }) {
  const [assigned, setAssigned] = useState(Array.isArray(initial) ? initial : []);
  const [all, setAll] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#0f172a");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/property-groups")
    .then(r => setAll(r.data.groups || []))
    .catch(() => setAll([]));
  useEffect(() => { load(); }, []);

  const toggle = async (slug, on) => {
    setBusy(true);
    try {
      const r = await api.put("/property-groups/assign", { address, group_slug: slug, assigned: on });
      setAssigned(r.data.groups || []);
      toast.success(on ? "Tag added" : "Tag removed");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const createGroup = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/property-groups", { name: newName.trim(), color: newColor });
      await load();
      // Auto-assign the just-created group
      await toggle(r.data.slug, true);
      setNewName(""); setNewColor("#0f172a"); setCreating(false);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const assignedGroups = all.filter(g => assigned.includes(g.slug));
  const unassignedGroups = all.filter(g => !assigned.includes(g.slug));

  return (
    <div className="mt-4 rugged-card p-4 border-l-4 border-fuchsia-500" data-testid="prop-groups-card">
      <div className="flex items-center gap-2">
        <Bookmark weight="fill" size={16} className="text-fuchsia-600"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Property Groups</h3>
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          {assigned.length} assigned
        </span>
        <button onClick={() => setCreating(v => !v)} data-testid="prop-groups-new-btn"
          className="ml-auto h-7 px-2 border-2 border-slate-300 hover:bg-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          {creating ? "Cancel" : <><Plus weight="bold" size={11}/> New group</>}
        </button>
      </div>

      {creating && (
        <div className="mt-3 p-3 bg-fuchsia-50 border-2 border-fuchsia-400" data-testid="prop-groups-new-form">
          <div className="flex items-center gap-2">
            <input value={newName} onChange={e => setNewName(e.target.value)}
              data-testid="prop-groups-new-name"
              placeholder="e.g. PMR_Markets, SFH, Active_RentCheck"
              className="h-9 flex-1 px-2 border-2 border-slate-300 text-sm"/>
            <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)}
              className="h-9 w-10 border-2 border-slate-300 cursor-pointer" title="Color"/>
            <button onClick={createGroup} disabled={busy || !newName.trim()} data-testid="prop-groups-new-create"
              className="h-9 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest">
              Create + tag
            </button>
          </div>
        </div>
      )}

      <div className="mt-3">
        {assignedGroups.length === 0 && !creating && (
          <div className="text-xs text-slate-500 font-bold">No tags yet — tap "New group" or pick one below.</div>
        )}
        {assignedGroups.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="prop-groups-assigned">
            {assignedGroups.map(g => (
              <span key={g.slug} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-black uppercase tracking-widest text-white"
                style={{ backgroundColor: g.color }}
                data-testid={`prop-group-chip-${g.slug}`}>
                <Tag weight="bold" size={10}/> {g.name}
                <button onClick={() => toggle(g.slug, false)} disabled={busy}
                  data-testid={`prop-group-remove-${g.slug}`}
                  className="ml-0.5 opacity-70 hover:opacity-100">
                  <X weight="bold" size={10}/>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {unassignedGroups.length > 0 && (
        <div className="mt-3 pt-3 border-t-2 border-dashed border-slate-200">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">Available tags · click to add</div>
          <div className="flex flex-wrap gap-1.5" data-testid="prop-groups-available">
            {unassignedGroups.map(g => (
              <button key={g.slug} onClick={() => toggle(g.slug, true)} disabled={busy}
                data-testid={`prop-group-add-${g.slug}`}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-black uppercase tracking-widest border-2 hover:bg-white"
                style={{ borderColor: g.color, color: g.color }}>
                <Plus weight="bold" size={10}/> {g.name}
                {g.member_count > 0 && <span className="opacity-60">({g.member_count})</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* ─── Property Keys Inventory + Check-Out Log ───────────────────────
   Physical key tracking per property. Each key has a label ("Key #4B"),
   description ("No Perm LB Allowed"), availability status, current holder,
   and a full history of check-outs / returns. AppFolio parity. */
function PropertyKeysCard({ address, initial }) {
  const [keys, setKeys] = useState(Array.isArray(initial) ? initial : []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ label: "", description: "" });
  const [checkoutFor, setCheckoutFor] = useState(null);   // key_id
  const [checkoutData, setCheckoutData] = useState({ holder_name: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const addKey = async () => {
    if (!form.label.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/property-keys/add",
        { address, label: form.label.trim(), description: form.description.trim() });
      setKeys([...keys, r.data.key]);
      setForm({ label: "", description: "" });
      setAdding(false);
      toast.success("Key added");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const checkout = async () => {
    if (!checkoutData.holder_name.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/property-keys/checkout",
        { address, key_id: checkoutFor, ...checkoutData });
      setKeys(r.data.keys);
      setCheckoutFor(null);
      setCheckoutData({ holder_name: "", note: "" });
      toast.success("Key checked out");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const returnKey = async (key_id) => {
    setBusy(true);
    try {
      const r = await api.post("/property-keys/return", { address, key_id, note: "" });
      setKeys(r.data.keys);
      toast.success("Key returned");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const removeKey = async (key_id, label) => {
    if (!confirm(`Remove ${label}? History will be lost.`)) return;
    setBusy(true);
    try {
      const r = await api.delete(`/property-keys/${key_id}`, { params: { address } });
      setKeys(r.data.keys);
      toast.success("Key removed");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const out = keys.filter(k => !k.available).length;

  return (
    <div className="mt-4 rugged-card p-4 border-l-4 border-amber-500" data-testid="prop-keys-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Wrench weight="fill" size={16} className="text-amber-700"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Keys</h3>
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          {keys.length} on file · {out} out
        </span>
        <button onClick={() => setAdding(v => !v)} data-testid="prop-keys-add-btn"
          className="ml-auto h-7 px-2 border-2 border-slate-300 hover:bg-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          {adding ? "Cancel" : <><Plus weight="bold" size={11}/> Add key</>}
        </button>
      </div>

      {adding && (
        <div className="mt-3 p-3 bg-amber-50 border-2 border-amber-400 space-y-2" data-testid="prop-keys-add-form">
          <input value={form.label} onChange={e => setForm({...form, label: e.target.value})}
            data-testid="prop-keys-label-input"
            placeholder='Label (e.g. "Key #4B" or "Front door")'
            className="h-9 w-full px-2 border-2 border-slate-300 text-sm font-mono"/>
          <input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
            placeholder='Description (e.g. "No Perm LB Allowed")'
            className="h-9 w-full px-2 border-2 border-slate-300 text-sm"/>
          <div className="flex justify-end">
            <button onClick={addKey} disabled={busy || !form.label.trim()} data-testid="prop-keys-save-btn"
              className="h-9 px-3 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest">
              Add key
            </button>
          </div>
        </div>
      )}

      {keys.length === 0 && !adding && (
        <div className="mt-2 text-xs text-slate-500 font-bold">No keys tracked yet — tap "Add key".</div>
      )}

      <div className="mt-3 space-y-2">
        {keys.map(k => (
          <div key={k.id} className={`p-2 border-2 ${k.available ? "border-emerald-400 bg-emerald-50" : "border-red-500 bg-red-50"}`}
               data-testid={`prop-key-${k.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-black text-sm">{k.label}</span>
              {k.available ? (
                <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white">
                  Available
                </span>
              ) : (
                <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white">
                  Out · {k.checked_out_to}
                </span>
              )}
              {k.description && <span className="text-xs text-slate-600">· {k.description}</span>}
              <button onClick={() => setExpanded({...expanded, [k.id]: !expanded[k.id]})}
                className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900">
                {expanded[k.id] ? "Hide history" : "History"}
              </button>
              {k.available ? (
                <button onClick={() => setCheckoutFor(k.id)} disabled={busy}
                  data-testid={`prop-key-checkout-${k.id}`}
                  className="h-7 px-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest">
                  Check out
                </button>
              ) : (
                <button onClick={() => returnKey(k.id)} disabled={busy}
                  data-testid={`prop-key-return-${k.id}`}
                  className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest">
                  Return
                </button>
              )}
              <button onClick={() => removeKey(k.id, k.label)} disabled={busy}
                className="h-7 px-2 border-2 border-slate-300 hover:bg-white text-slate-600 font-black uppercase text-[10px] tracking-widest">
                <X weight="bold" size={10}/>
              </button>
            </div>
            {!k.available && k.checked_out_at && (
              <div className="mt-1 text-[10px] text-red-700 font-mono">
                Out since {new Date(k.checked_out_at).toLocaleString()}
                {k.checked_out_note && ` · ${k.checked_out_note}`}
              </div>
            )}
            {checkoutFor === k.id && (
              <div className="mt-2 p-2 bg-white border border-slate-300 space-y-1.5" data-testid={`prop-key-checkout-form-${k.id}`}>
                <input value={checkoutData.holder_name}
                  data-testid="prop-key-holder-input"
                  onChange={e => setCheckoutData({...checkoutData, holder_name: e.target.value})}
                  placeholder="Holder name (e.g. Jesse)"
                  className="h-8 w-full px-2 border-2 border-slate-300 text-sm"/>
                <input value={checkoutData.note}
                  onChange={e => setCheckoutData({...checkoutData, note: e.target.value})}
                  placeholder="Note (optional — reason, expected return)"
                  className="h-8 w-full px-2 border-2 border-slate-300 text-sm"/>
                <div className="flex justify-end gap-1">
                  <button onClick={() => setCheckoutFor(null)}
                    className="h-7 px-2 border-2 border-slate-300 hover:bg-slate-50 font-black uppercase text-[10px] tracking-widest">
                    Cancel
                  </button>
                  <button onClick={checkout} disabled={busy || !checkoutData.holder_name.trim()}
                    data-testid="prop-key-checkout-confirm"
                    className="h-7 px-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest">
                    Log check-out
                  </button>
                </div>
              </div>
            )}
            {expanded[k.id] && (k.history || []).length > 0 && (
              <div className="mt-2 space-y-0.5">
                {(k.history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="text-[10px] font-mono text-slate-700">
                    · {new Date(h.at).toLocaleString()} — <b>{h.action}</b>
                    {h.holder && ` by ${h.holder}`}
                    {h.prior_holder && ` from ${h.prior_holder}`}
                    {h.by && ` (logged by ${h.by})`}
                    {h.note && ` — ${h.note}`}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


/* ─── Per-Property Audit Log ─────────────────────────────────────────
   Rolls up every activity_log row touching this address. Compliance edits,
   group tag changes, key check-outs, WO creations, walks — all in reverse-
   chrono. This is the "who changed what and when" transparency AppFolio
   surfaces on its property page. */
function PropertyAuditLogCard({ address }) {
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!address) return;
    api.get("/property-audit", { params: { address, limit: 200 } })
      .then(r => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [address]);

  if (items === null) return null;
  const visible = expanded ? items : items.slice(0, 8);
  return (
    <div className="mt-4 rugged-card p-4 border-l-4 border-slate-500" data-testid="prop-audit-card">
      <div className="flex items-center gap-2">
        <ClockCounterClockwise weight="bold" size={16} className="text-slate-700"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">History</h3>
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          {items.length} events
        </span>
      </div>
      {items.length === 0 ? (
        <div className="mt-2 text-xs text-slate-500 font-bold">No activity recorded on this property yet.</div>
      ) : (
        <>
          <div className="mt-2 space-y-1.5">
            {visible.map(a => (
              <div key={a.id} className="text-xs" data-testid={`prop-audit-row-${a.id}`}>
                <span className="font-mono text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
                <span className="mx-1.5 font-black uppercase tracking-wider text-slate-700">{a.action.replace(/_/g, " ")}</span>
                <span className="text-slate-600">— {a.entity_label}</span>
                <span className="ml-1.5 text-[10px] text-slate-500">· {a.user_name || "system"}</span>
              </div>
            ))}
          </div>
          {items.length > 8 && (
            <button onClick={() => setExpanded(v => !v)} data-testid="prop-audit-toggle"
              className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900">
              {expanded ? "Show less" : `Show all ${items.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function PropertyMediaCard({ address, initial }) {
  const [items, setItems] = useState(Array.isArray(initial) ? initial : []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ kind: "matterport", label: "", url: "", room: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [inlineId, setInlineId] = useState(null);   // media id currently expanded inline

  // Convert a public URL into an embeddable iframe src.
  const embedSrc = (m) => {
    const url = m.url || "";
    if (m.kind === "video") {
      // YouTube short link
      const ytShort = url.match(/youtu\.be\/([\w-]+)/);
      if (ytShort) return `https://www.youtube.com/embed/${ytShort[1]}?autoplay=1`;
      const ytFull = url.match(/[?&]v=([\w-]+)/);
      if (ytFull) return `https://www.youtube.com/embed/${ytFull[1]}?autoplay=1`;
      const vim = url.match(/vimeo\.com\/(\d+)/);
      if (vim) return `https://player.vimeo.com/video/${vim[1]}?autoplay=1`;
    }
    if (m.kind === "matterport") {
      // Matterport supports iframe embed if you pass ?m=<hash>&play=1
      if (url.includes("matterport.com")) {
        return url.includes("play=1") ? url : (url + (url.includes("?") ? "&play=1" : "?play=1"));
      }
    }
    // 360 / gallery / other — just try the URL directly
    return url;
  };

  const kindMeta = {
    matterport: { label: "Matterport", color: "bg-cyan-600",    hint: "3D walk-through" },
    "360":      { label: "360°",       color: "bg-fuchsia-600", hint: "spherical photo" },
    video:      { label: "Video",      color: "bg-red-600",     hint: "YouTube / Vimeo / MP4" },
    gallery:    { label: "Gallery",    color: "bg-amber-600",   hint: "photo album link" },
    other:      { label: "Link",       color: "bg-slate-500",   hint: "any URL" },
  };

  const add = async () => {
    if (!form.label.trim() || !form.url.trim()) return toast.error("Label + URL required");
    setBusy(true);
    try {
      const r = await api.post("/property-media", { address, ...form });
      setItems([...items, r.data.media]);
      setForm({ kind: "matterport", label: "", url: "", room: "", note: "" });
      setAdding(false);
      toast.success("Media added");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const remove = async (id, label) => {
    if (!confirm(`Remove "${label}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/property-media/${id}`, { params: { address } });
      setItems(items.filter(i => i.id !== id));
      toast.success("Removed");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rugged-card p-4 border-l-4 border-cyan-500" data-testid="prop-media-card">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Property Media</h3>
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          Matterport · 360° · Video · Gallery — techs preview before roll-out
        </span>
        <button onClick={() => setAdding(v => !v)} data-testid="prop-media-add-btn"
          className="ml-auto h-7 px-2 border-2 border-slate-300 hover:bg-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          {adding ? "Cancel" : "+ Add media"}
        </button>
      </div>
      {adding && (
        <div className="mt-3 p-3 bg-cyan-50 border-2 border-cyan-400 space-y-2" data-testid="prop-media-form">
          <div className="grid grid-cols-2 gap-2">
            <select value={form.kind} data-testid="prop-media-kind"
              onChange={e => setForm({...form, kind: e.target.value})}
              className="h-9 px-2 border-2 border-slate-300 text-sm font-medium bg-white">
              {Object.entries(kindMeta).map(([k, m]) => (
                <option key={k} value={k}>{m.label} — {m.hint}</option>
              ))}
            </select>
            <input value={form.room} onChange={e => setForm({...form, room: e.target.value})}
              placeholder="Room / Area (optional)"
              className="h-9 px-2 border-2 border-slate-300 text-sm"/>
          </div>
          <input value={form.label} data-testid="prop-media-label"
            onChange={e => setForm({...form, label: e.target.value})}
            placeholder='Label (e.g. "Whole Unit Matterport 2024")'
            className="h-9 w-full px-2 border-2 border-slate-300 text-sm"/>
          <input value={form.url} data-testid="prop-media-url"
            onChange={e => setForm({...form, url: e.target.value})}
            placeholder="https://my.matterport.com/show/?m=..."
            className="h-9 w-full px-2 border-2 border-slate-300 text-sm font-mono"/>
          <input value={form.note} onChange={e => setForm({...form, note: e.target.value})}
            placeholder="Note (optional)"
            className="h-9 w-full px-2 border-2 border-slate-300 text-sm"/>
          <div className="flex justify-end">
            <button onClick={add} disabled={busy || !form.label.trim() || !form.url.trim()}
              data-testid="prop-media-save"
              className="h-9 px-3 bg-cyan-700 hover:bg-cyan-800 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest">
              Add
            </button>
          </div>
        </div>
      )}
      {items.length === 0 && !adding && (
        <div className="mt-2 text-xs text-slate-500 font-bold">
          No media yet — add Matterport tours, 360° photos, or marketing videos so techs can prep before arrival.
        </div>
      )}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map(m => {
          const meta = kindMeta[m.kind] || kindMeta.other;
          const isOpen = inlineId === m.id;
          return (
            <div key={m.id} className={`p-2 border-2 bg-white ${isOpen ? "border-cyan-500 sm:col-span-2" : "border-slate-300 hover:border-cyan-400"}`} data-testid={`prop-media-${m.id}`}>
              <div className="flex items-start gap-2">
                <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${meta.color} text-white flex-shrink-0`}>
                  {meta.label}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-black text-sm truncate">{m.label}</div>
                  {m.room && <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{m.room}</div>}
                  {m.note && <div className="text-xs text-slate-600 mt-0.5">{m.note}</div>}
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {new Date(m.added_at).toLocaleDateString()} · {m.added_by_name}
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    <button onClick={() => setInlineId(isOpen ? null : m.id)}
                      data-testid={`prop-media-inline-${m.id}`}
                      className="h-7 px-2 bg-cyan-700 hover:bg-cyan-800 text-white font-black uppercase text-[10px] tracking-widest">
                      {isOpen ? "Close" : "View inline"}
                    </button>
                    <a href={m.url} target="_blank" rel="noreferrer"
                      data-testid={`prop-media-open-${m.id}`}
                      className="h-7 px-2 border-2 border-slate-300 hover:bg-slate-50 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-0.5">
                      Open ↗
                    </a>
                  </div>
                </div>
                <button onClick={() => remove(m.id, m.label)} disabled={busy}
                  className="text-slate-400 hover:text-red-600 flex-shrink-0" title="Remove">×</button>
              </div>
              {isOpen && (
                <div className="mt-2 aspect-video bg-black" data-testid={`prop-media-iframe-${m.id}`}>
                  <iframe src={embedSrc(m)} title={m.label}
                    className="w-full h-full"
                    allow="xr-spatial-tracking; fullscreen; autoplay; encrypted-media"
                    allowFullScreen/>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Property Financials Rollup ────────────────────────────────────
   Grand total spend + every receipt/quote/invoice ever attached to any WO
   or property file at this address. Powers the "we've already spent $8k
   on 1026 Irving" conversation before approving another $2k of work. */
function PropertyFinancialsCard({ address }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("all");

  useEffect(() => {
    if (!address) return;
    api.get("/property-financials", { params: { address } })
      .then(r => setData(r.data))
      .catch(() => setData({ counts: { receipts: 0, quotes: 0, invoices: 0 }, total_spent_cents: 0, quotes_pending_cents: 0, receipts: [], quotes: [], invoices: [], vendors: [] }));
  }, [address]);

  if (!data) return null;
  const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  const all = [
    ...data.receipts.map(r => ({ ...r, _type: "receipt" })),
    ...data.quotes.map(q => ({ ...q, _type: "quote" })),
    ...data.invoices.map(i => ({ ...i, _type: "invoice" })),
  ].sort((a, b) => (b.at || b.uploaded_at || "").localeCompare(a.at || a.uploaded_at || ""));
  const rows = view === "all" ? all : all.filter(r => r._type === view);
  const typeColor = { receipt: "bg-amber-600", quote: "bg-cyan-600", invoice: "bg-red-600" };

  return (
    <div className="mt-4 rugged-card p-4 border-l-4 border-emerald-500" data-testid="prop-financials-card">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Quotes &amp; Receipts</h3>
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          Grand-total spend on this property
        </span>
        <a href={`/admin/jobs?property=${encodeURIComponent(address)}`}
          data-testid="prop-request-quote-btn"
          className="ml-auto h-7 px-2 border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          + Request quote
        </a>
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Total spent" value={money(data.total_spent_cents)} tone="emerald" testid="fin-total-spent"/>
        <Stat label="Quotes pending" value={money(data.quotes_pending_cents)} tone="cyan" testid="fin-quotes-pending"/>
        <Stat label="Receipts" value={data.counts.receipts} tone="amber" testid="fin-receipts-count"/>
        <Stat label="Invoices" value={data.counts.invoices} tone="red" testid="fin-invoices-count"/>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <FilterChip active={view==="all"} onClick={() => setView("all")} label="All" count={all.length} tone="slate" testid="fin-filter-all"/>
        <FilterChip active={view==="receipt"} onClick={() => setView("receipt")} label="Receipts" count={data.counts.receipts} tone="amber" testid="fin-filter-receipts"/>
        <FilterChip active={view==="quote"} onClick={() => setView("quote")} label="Quotes" count={data.counts.quotes} tone="emerald" testid="fin-filter-quotes"/>
        <FilterChip active={view==="invoice"} onClick={() => setView("invoice")} label="Invoices" count={data.counts.invoices} tone="red" testid="fin-filter-invoices"/>
      </div>

      {rows.length === 0 && (
        <div className="mt-3 text-xs text-slate-500 font-bold">No financial records at this address yet.</div>
      )}
      <div className="mt-3 space-y-1.5">
        {rows.map(r => (
          <div key={`${r._type}-${r.id}`} className="p-2 border-2 border-slate-200 bg-white flex items-center gap-2 flex-wrap text-xs" data-testid={`fin-row-${r._type}-${r.id}`}>
            <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${typeColor[r._type]} text-white`}>{r._type}</span>
            <span className="font-black">{r.label || r.vendor || r.work_order || "—"}</span>
            {r.vendor && r.vendor !== r.label && <span className="text-slate-600">· {r.vendor}</span>}
            {r.work_order && <span className="text-slate-500 font-mono">· {r.work_order}</span>}
            <span className="ml-auto font-mono font-black">
              {r.amount_cents ? money(r.amount_cents) : "—"}
            </span>
            <span className="text-slate-500 font-mono text-[10px]">
              {(r.at || r.uploaded_at || "").slice(0, 10)}
            </span>
            {r.url && (
              <a href={r.url} target="_blank" rel="noreferrer"
                className="text-cyan-700 font-black uppercase text-[9px] tracking-widest hover:underline">Open ↗</a>
            )}
            {r.job_id && (
              <a href={`/admin/jobs/${r.job_id}`}
                className="text-slate-700 font-black uppercase text-[9px] tracking-widest hover:underline">WO ↗</a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const tones = {
    emerald: "border-emerald-500 bg-emerald-50 text-emerald-800",
    cyan:    "border-cyan-500 bg-cyan-50 text-cyan-800",
    amber:   "border-amber-500 bg-amber-50 text-amber-800",
    red:     "border-red-500 bg-red-50 text-red-800",
  };
  return (
    <div className={`p-2 border-l-4 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-mono font-black text-lg leading-none mt-0.5">{value}</div>
    </div>
  );
}

/* Units panel — dense grid for large buildings (5346 Chew Ave has 43 units,
   scrolling a long single column was awful). When >12 units we switch to a
   compact 6-col chip grid + a filter input to jump to a unit fast. */
function PropertyUnitsPanel({ data, selectedUnit, setSelectedUnit, s }) {
  const [q, setQ] = useState("");
  const [busyCommon, setBusyCommon] = useState(false);
  const allUnits = data.units || [];
  const realUnits = allUnits.filter(u => u.unit && u.unit.toLowerCase() !== "common");
  const hasCommon = allUnits.some(u => (u.unit || "").toLowerCase() === "common");
  const showCreateCommon = realUnits.length >= 2 && !hasCommon;
  const isBig = realUnits.length > 12;
  const filtered = q
    ? allUnits.filter(u => (u.unit || "").toLowerCase().includes(q.toLowerCase())
        || (!u.unit && "building".includes(q.toLowerCase())))
    : allUnits;

  const createCommon = async () => {
    if (!window.confirm(`Create a "Common area" unit for ${data.address}?\n\nUse this to tag building-wide assets (boiler, water heater, fire extinguishers, cameras) and meters that serve multiple units.`)) return;
    setBusyCommon(true);
    try {
      const r = await api.post("/admin/bootstrap/common-area", { address: data.address });
      if (r.data.already_existed) {
        toast.success("Common area already exists — jumping there");
      } else {
        toast.success("Common area created");
      }
      // Hard reload the property so the new unit shows up in the panel
      window.location.href = `/admin/properties/${encodeURIComponent(data.address)}/unit/Common`;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not create common area"));
      setBusyCommon(false);
    }
  };

  const gridCls = isBig
    ? "grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5"
    : "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2";

  return (
    <div className="mt-4 rugged-card p-4 bg-white" data-testid="prop-units-panel">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Buildings weight="bold" size={18} className="text-fuchsia-600"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Units in this building</h3>
        <span className="text-xs text-slate-500 font-bold">{s.unit_count || 0}</span>
        {showCreateCommon && (
          <button onClick={createCommon} disabled={busyCommon}
            className="ml-2 inline-flex items-center gap-1 h-7 px-2.5 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-50"
            data-testid="prop-add-common-area-btn"
            title="Create a Common Area unit for building-wide assets/meters (boiler, water heater, fire extinguishers, shared meters)">
            + Common Area
          </button>
        )}
        {isBig && (
          <input value={q} onChange={e => setQ(e.target.value)}
            data-testid="prop-units-search"
            placeholder="Filter (e.g. 3B, PH, 12)"
            className="h-8 flex-1 sm:max-w-xs px-2 border-2 border-slate-300 text-sm font-mono"/>
        )}
        {selectedUnit !== null && (
          <button onClick={() => setSelectedUnit(null)} data-testid="prop-unit-clear"
            className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#0f172a]">
            ← Show All
          </button>
        )}
      </div>
      <div className={gridCls}>
        {filtered.map((u, i) => {
          const key = u.unit || "";
          const isActive = selectedUnit === key;
          const unitHref = u.unit
            ? `/admin/properties/${encodeURIComponent(data.address)}/unit/${encodeURIComponent(u.unit)}`
            : null;
          if (isBig) {
            // Compact chip view — just unit label + open badge on a tiny tile
            const chipCls = `px-2 py-1.5 border-2 text-center transition-colors block ${
              isActive ? "border-[#00C4CC] bg-cyan-50"
                : u.unit ? "border-fuchsia-500 bg-white hover:bg-fuchsia-50"
                         : "border-slate-300 bg-white hover:bg-slate-50"
            }`;
            const ChipInner = (
              <>
                <div className={`font-display font-black text-sm leading-none ${u.unit ? "text-fuchsia-700" : "text-slate-700"}`}>
                  {u.unit || "—"}
                </div>
                <div className="mt-1 text-[9px] font-black uppercase tracking-widest text-slate-500 flex items-center justify-center gap-1">
                  <span>{u.jobs}j</span>
                  {u.open > 0 && <span className="text-emerald-600">{u.open}o</span>}
                  {u.vacancy_status && !["occupied","unknown",""].includes((u.vacancy_status || "").toLowerCase()) && <span className="text-red-600">●</span>}
                  {u.has_ring_alarm && <span className="text-red-600">◉</span>}
                </div>
              </>
            );
            return unitHref
              ? <Link key={i} to={unitHref} data-testid={`prop-unit-${(u.unit || 'base').toLowerCase()}`} className={chipCls}>{ChipInner}</Link>
              : <button key={i} onClick={() => setSelectedUnit(isActive ? null : key)} data-testid={`prop-unit-${(u.unit || 'base').toLowerCase()}`} className={chipCls}>{ChipInner}</button>;
          }
          // Original rich card for buildings ≤ 12 units
          const cardCls = `p-3 border-2 text-left transition-all block ${
            isActive
              ? "border-[#00C4CC] bg-cyan-50 shadow-[3px_3px_0px_#0f172a] -translate-y-0.5"
              : (u.unit ? "border-fuchsia-600 bg-white hover:bg-fuchsia-50" : "border-slate-300 bg-white hover:bg-slate-50")
          }`;
          const Inner = (
            <>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center justify-between">
                <span>{u.unit ? "Unit" : "Building (no unit)"}</span>
                {u.vacancy_status && !["occupied","unknown",""].includes((u.vacancy_status || "").toLowerCase()) && (
                  <span className="text-red-600">● {(u.vacancy_status || "").replace("_"," ").toUpperCase()}</span>
                )}
              </div>
              <div className={`mt-0.5 font-display font-black text-lg leading-none ${u.unit ? "text-fuchsia-700" : "text-slate-700"}`}>
                {u.unit || "—"}
              </div>
              <div className="mt-1.5 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest flex-wrap">
                <span className="text-slate-500">{u.jobs} job{u.jobs === 1 ? "" : "s"}</span>
                {u.open > 0 && <span className="text-emerald-600">{u.open} open</span>}
                {u.turns > 0 && <span className="text-amber-600">{u.turns} turn</span>}
                {u.assets_count > 0 && <span className="text-purple-600">{u.assets_count} asset{u.assets_count === 1 ? "" : "s"}</span>}
                {u.has_ring_alarm && <span className="text-red-600">◉ RING</span>}
              </div>
              <div className="mt-1 text-[9px] font-black uppercase tracking-widest text-[#00C4CC]">Open unit →</div>
            </>
          );
          return unitHref
            ? <Link key={i} to={unitHref} data-testid={`prop-unit-${(u.unit || 'base').toLowerCase()}`} className={cardCls}>{Inner}</Link>
            : <button key={i} onClick={() => setSelectedUnit(isActive ? null : key)} data-testid={`prop-unit-${(u.unit || 'base').toLowerCase()}`} className={cardCls}>{Inner}</button>;
        })}
      </div>
    </div>
  );
}
